import { Controller } from "@hotwired/stimulus";

/**
 * Explicitly renders Turnstile widgets after Turbo navigations.
 *
 * Auth forms opt into formGuardValue. In that mode the first submit is held
 * until Turnstile has a token, then replayed with the same form and submitter.
 * The form is never rebuilt or cleared, so passwords and other entered values
 * survive a challenge, an expiry, or a retry.
 *
 * Other callers continue to use this controller as the explicit-render safety
 * net they had before. Their existing Arena, advisor, and reaction controllers
 * remain responsible for their own verification flows.
 */

type TurnstileState =
  | "idle"
  | "ready"
  | "interactive"
  | "success"
  | "error"
  | "expiry"
  | "timeout"
  | "unsupported-browser"
  | "reset"
  | "resubmission";

type TurnstileApi = {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string | HTMLElement) => void;
  remove: (widgetId: string) => void;
  getResponse?: (widgetId: string) => string | null;
  execute?: (widgetId: string | HTMLElement) => void;
  ready?: (callback: () => void) => void;
};

const MAX_RETRIES = 10;
const RETRY_INTERVAL_MS = 200;

const STATE_MESSAGES: Record<TurnstileState, string> = {
  idle: "Security check is loading.",
  ready: "Security check ready.",
  interactive: "Please complete the security check.",
  success: "Security check complete.",
  error: "Security check failed. Try again.",
  expiry: "Security check expired. Try again.",
  timeout: "Security check timed out. Try again.",
  "unsupported-browser": "This browser cannot complete the security check. Try another browser.",
  reset: "Security check reset.",
  resubmission: "Checking your submission...",
};

const RETRYABLE_STATES = new Set<TurnstileState>(["error", "expiry", "timeout"]);
const VISIBLE_FAILURE_STATES = new Set<TurnstileState>([
  "error",
  "expiry",
  "timeout",
  "unsupported-browser",
]);

export default class extends Controller<HTMLElement> {
  static targets = ["status", "retry"];

  static values = {
    verifyUrl: { type: String, default: "" },
    formGuard: { type: Boolean, default: false },
  };

  declare verifyUrlValue: string;
  declare formGuardValue: boolean;
  declare readonly hasStatusTarget: boolean;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasRetryTarget: boolean;
  declare readonly retryTarget: HTMLButtonElement;

  private widgetId: string | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryCount = 0;
  private state: TurnstileState = "idle";
  private turnstileScript: HTMLScriptElement | null = null;
  private scriptLoaded = false;
  private connected = false;
  private form: HTMLFormElement | null = null;
  private pendingSubmission = false;
  private pendingSubmitter: HTMLButtonElement | HTMLInputElement | null = null;
  private resubmitting = false;

  connect(): void {
    this.connected = true;
    if (this.formGuardValue) this.setState("idle");

    this.turnstileScript = document.querySelector<HTMLScriptElement>(
      'script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]'
    );
    this.scriptLoaded =
      Boolean(this.turnstileApi) || !this.turnstileScript || document.readyState === "complete";
    this.turnstileScript?.addEventListener("load", this.handleScriptLoad);
    this.turnstileScript?.addEventListener("error", this.handleScriptError);

    if (this.formGuardValue) {
      this.form = this.element.closest<HTMLFormElement>("form");
      this.form?.addEventListener("submit", this.handleFormSubmit, true);
    }

    document.addEventListener("turbo:before-cache", this.handleBeforeCache);
    this.renderWidget();
  }

  disconnect(): void {
    this.connected = false;
    this.clearRetryTimer();
    this.turnstileScript?.removeEventListener("load", this.handleScriptLoad);
    this.turnstileScript?.removeEventListener("error", this.handleScriptError);
    this.turnstileScript = null;
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache);
    this.form?.removeEventListener("submit", this.handleFormSubmit, true);
    this.form = null;

    const api = this.turnstileApi;
    if (this.widgetId && api) {
      api.remove(this.widgetId);
      this.widgetId = null;
    }
  }

  /** Public action — can be invoked via data-action="turnstile#render". */
  render(): void {
    this.renderWidget();
  }

  /** Public action — resets the current widget after a failed verification. */
  reset(): void {
    this.resetWidget();
    this.setState("reset");
  }

  /** Public action — retries a failed or expired challenge without clearing inputs. */
  retry(): void {
    this.clearRetryTimer();

    const api = this.turnstileApi;
    if (!api) {
      this.reloadPage();
      return;
    }

    // The API is the source of truth. Its script load event may have fired
    // before this controller connected, or before a failed attempt recovered.
    this.scriptLoaded = true;

    if (!this.widgetId) {
      this.setState("idle");
      this.renderWidget();
      return;
    }

    this.resetWidget();
    this.setState("reset");

    const container = this.container;
    if (container?.dataset.execution === "execute" && api.execute) {
      api.execute(this.widgetId);
    }
  }

  // ---------------------------------------------------------------------------

  private get turnstileApi(): TurnstileApi | undefined {
    return (window as Window & { turnstile?: unknown }).turnstile as TurnstileApi | undefined;
  }

  private get container(): HTMLElement | null {
    return this.element.querySelector<HTMLElement>(".cf-turnstile");
  }

  private renderWidget(): void {
    const container = this.container;
    if (!container || this.widgetId || container.querySelector("iframe")) return;

    const api = this.turnstileApi;
    if (api) this.scriptLoaded = true;

    if (!api || (this.turnstileScript && !this.scriptLoaded)) {
      this.scheduleRetry();
      return;
    }

    this.clearRetryTimer();

    this.markReady();
    this.renderWidgetNow();
  }

  private handleScriptLoad = (): void => {
    if (!this.connected) return;

    this.scriptLoaded = true;
    this.renderWidget();
  };

  private handleScriptError = (): void => {
    if (!this.connected) return;
    if (this.turnstileApi) {
      this.scriptLoaded = true;
      this.renderWidget();
      return;
    }

    this.clearRetryTimer();
    this.onError("script-load");
  };

  private handleBeforeCache = (): void => {
    if (!this.connected) return;

    // Turbo snapshots can be restored later. Remove the iframe and one-shot
    // token from the snapshot, but leave every user-entered form value alone.
    this.clearRetryTimer();
    this.pendingSubmission = false;
    this.pendingSubmitter = null;
    this.resubmitting = false;
    this.clearResponseToken();

    const api = this.turnstileApi;
    if (this.widgetId && api) {
      api.remove(this.widgetId);
      this.widgetId = null;
    }

    this.setState("reset");
  };

  private renderWidgetNow(): void {
    const container = this.container;
    const api = this.turnstileApi;
    const sitekey = container?.dataset.sitekey;
    if (!container || !api || this.widgetId || !sitekey) return;

    const options: Record<string, unknown> = {
      sitekey,
      size: container.dataset.size || "normal",
      theme: container.dataset.theme || "auto",
      callback: (token: string) => this.onSolved(token),
      "error-callback": (error: unknown) => this.onError(error),
      "expired-callback": () => this.onExpiry(),
      "timeout-callback": () => this.onTimeout(),
      "before-interactive-callback": () => this.onInteractive(),
      "after-interactive-callback": () => this.onAfterInteractive(),
      "unsupported-callback": () => this.onUnsupportedBrowser(),
    };

    this.addStringOption(options, "action", container.dataset.action);
    this.addStringOption(options, "appearance", container.dataset.appearance);
    this.addStringOption(options, "execution", container.dataset.execution);
    this.addStringOption(options, "retry", container.dataset.retry);
    this.addStringOption(options, "refresh-expired", container.dataset.refreshExpired);
    this.addStringOption(options, "refresh-timeout", container.dataset.refreshTimeout);
    this.addStringOption(options, "response-field-name", container.dataset.responseFieldName);

    if (container.dataset.responseField) {
      options["response-field"] = container.dataset.responseField === "true";
    }
    if (container.dataset.tabindex) {
      options.tabindex = Number(container.dataset.tabindex);
    }

    try {
      this.widgetId = api.render(container, options);
    } catch (error) {
      this.onError(error);
    }
  }

  private addStringOption(options: Record<string, unknown>, name: string, value?: string): void {
    if (value) options[name] = value;
  }

  private markReady(): void {
    if (!this.pendingSubmission) this.setState("ready");
  }

  /** Called when the Turnstile challenge is solved. */
  private onSolved(token: string): void {
    if (!token) {
      this.onError("empty-token");
      return;
    }

    this.setResponseToken(token);
    this.setState("success");

    if (!this.verifyUrlValue) {
      this.resubmitPendingForm();
      return;
    }

    void this.verifyToken(token);
  }

  private async verifyToken(token: string): Promise<void> {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    try {
      const response = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
        },
        body: JSON.stringify({ "cf-turnstile-response": token }),
      });

      if (!response.ok) throw new Error("Turnstile verification failed: " + response.status);

      this.dispatch("verified", { detail: { token } });
      this.resubmitPendingForm();
    } catch {
      this.clearResponseToken();
      this.setState("error");
      this.resetWidget();
    }
  }

  private onInteractive(): void {
    this.setState("interactive");
  }

  private onAfterInteractive(): void {
    if (this.state === "interactive") this.markReady();
  }

  private onError(error: unknown): void {
    this.clearResponseToken();
    this.setState("error", { error });
  }

  private onExpiry(): void {
    this.clearResponseToken();
    this.setState("expiry");
  }

  private onTimeout(): void {
    this.clearResponseToken();
    this.setState("timeout");
  }

  private onUnsupportedBrowser(): void {
    this.clearResponseToken();
    this.setState("unsupported-browser");
  }

  private setState(state: TurnstileState, detail: Record<string, unknown> = {}): void {
    this.state = state;
    this.element.dataset.turnstileState = state;

    if (this.hasStatusTarget) {
      this.statusTarget.textContent = STATE_MESSAGES[state];
      this.statusTarget.classList.toggle("sr-only", !VISIBLE_FAILURE_STATES.has(state));
    }

    if (this.hasRetryTarget) {
      const retryable = RETRYABLE_STATES.has(state);
      this.retryTarget.hidden = !retryable;
      this.retryTarget.classList.toggle("hidden", !retryable);
    }

    if (this.form) {
      if (state === "resubmission") {
        this.form.setAttribute("aria-busy", "true");
      } else {
        this.form.removeAttribute("aria-busy");
      }
    }

    this.dispatch(state, { detail: { state, ...detail } });
  }

  private handleFormSubmit = (event: Event): void => {
    if (!this.formGuardValue || this.resubmitting || !this.form) return;

    const token = this.currentToken();
    if (token) {
      this.setResponseToken(token);
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.state === "unsupported-browser") {
      this.setState("unsupported-browser");
      return;
    }

    const submitter = (event as SubmitEvent).submitter;
    this.pendingSubmitter =
      submitter instanceof HTMLButtonElement || submitter instanceof HTMLInputElement
        ? submitter
        : null;
    this.pendingSubmission = true;

    if (RETRYABLE_STATES.has(this.state) || this.state === "success") {
      this.resetWidget();
    }

    this.setState("resubmission");
    this.requestChallenge();
  };

  private requestChallenge(): void {
    const container = this.container;
    const api = this.turnstileApi;
    if (!container || !api) {
      this.renderWidget();
      return;
    }

    if (!this.widgetId) {
      this.renderWidget();
      return;
    }

    if (container.dataset.execution === "execute") {
      if (api.execute) {
        api.execute(this.widgetId);
      } else {
        this.setState("error");
      }
    }
  }

  private resubmitPendingForm(): void {
    if (!this.pendingSubmission || this.resubmitting || !this.form) return;

    const form = this.form;
    const submitter = this.pendingSubmitter;
    this.pendingSubmission = false;
    this.resubmitting = true;
    this.setState("resubmission");

    window.requestAnimationFrame(() => {
      if (!this.connected || !this.form) {
        this.resubmitting = false;
        this.pendingSubmitter = null;
        return;
      }

      try {
        if (submitter && submitter.form === form && !submitter.disabled) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      } finally {
        this.resubmitting = false;
        this.pendingSubmitter = null;
      }
    });
  }

  private currentToken(): string | null {
    const input = this.responseInput();
    if (input?.value) return input.value;

    if (this.widgetId) {
      return this.turnstileApi?.getResponse?.(this.widgetId) || null;
    }

    return null;
  }

  private responseInput(): HTMLInputElement | null {
    const name = this.container?.dataset.responseFieldName || "cf-turnstile-response";
    return (
      this.container?.querySelector<HTMLInputElement>('input[name="' + name + '"]') ||
      this.form?.querySelector<HTMLInputElement>('input[name="' + name + '"]') ||
      null
    );
  }

  private setResponseToken(token: string): void {
    const input = this.responseInput();
    if (input) {
      input.value = token;
      return;
    }

    const form = this.form || this.element.closest<HTMLFormElement>("form");
    if (!form) return;

    const hiddenInput = document.createElement("input");
    hiddenInput.type = "hidden";
    hiddenInput.name = this.container?.dataset.responseFieldName || "cf-turnstile-response";
    hiddenInput.value = token;
    this.element.appendChild(hiddenInput);
  }

  private clearResponseToken(): void {
    const input = this.responseInput();
    if (input) input.value = "";
  }

  private resetWidget(): void {
    this.clearResponseToken();

    const api = this.turnstileApi;
    if (this.widgetId && api) api.reset(this.widgetId);
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;

    if (this.retryCount >= MAX_RETRIES) {
      this.onError("api-unavailable");
      return;
    }

    this.retryCount++;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.renderWidget();
    }, RETRY_INTERVAL_MS);
  }

  private clearRetryTimer(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryCount = 0;
  }

  private reloadPage(): void {
    window.location.reload();
  }
}
